import { HotelResponse } from '@/types/benefits/hotel/HotelResponse';
import { zodResolver } from '@hookform/resolvers/zod';
import { startTransition, useEffect, useMemo, useRef, useState } from 'react';
import {
  PastedFieldsProvider,
  usePastedFieldsState,
} from '@/contexts/PastedFieldsContext';
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';
import StyledContainer from '../styled/StyledContainer';
import { getHotelCategoryName } from '@/utils/benefits/getHotelCategoryName';
import { useDuplicateHotel, useUpdateHotel } from '@/hooks/useHotel';
import { SupplierStatus } from '@/types/suppliers/SupplierStatus';
import { PATHS } from '@/constants/Paths';
import { buildBenefitsListSearch } from '@/utils/searchParams/buildBenefitsListSearch';
import { getListSearchFromLocation } from '@/utils/searchParams/getListSearchFromLocation';
import StyledFormHeaderWithBreadcrumb from '../styled/StyledFormHeaderWithBreadcrumb';
import { formatBenefitCode } from '@/utils/displayCodes';
import BenefitTypeCell from '../benefits/BenefitTypeCell';
import StyledButton from '../styled/StyledButton';
import StyledButtonLoader from '../styled/StyledButtonLoader';
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import EditIcon from '@/assets/icons/edit-01.svg?react';
import CopyIcon from '@/assets/icons/copy-03.svg?react';
import SaveIcon from '@/assets/icons/save-02.svg?react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import StyledStepper from '../styled/StyledStepper';
import { ArrowRightIcon } from 'lucide-react';
import HotelRoomsCategoriesList from './roomCategories/HotelRoomsCategoriesList';
import HotelPricingSummary from './HotelPricingSummary';
import clsx from 'clsx';
import { getFileKeyFromUrl } from '@/utils/s3/getFileKeyFromUrl';
import BenefitDeletionModal from '@/components/benefits/BenefitDeletionModal';
import ModalDuplication from '../ui/ModalDuplication';
import { HOTEL_DUPLICATION_FIELDS } from '@/constants/benefitFields';
import { getAllPricesPartitions } from '@/utils/benefits/getHotelPrices';
import { loadHotelDefaultValues } from '@/utils/benefits/loadHotelDefaultValues';
import { useToast } from '@/hooks/useToast';
import HotelConfigurationStep from './HotelConfigurationStep';
import { numberIfNotUndefined } from '@/utils/prices/numberIfNotUndefined';
import { serializePensionPricings } from '@/utils/benefits/hotel/serializePensionPricings';
import { useTranslation } from '@/i18n';
import { HOTEL_STEPS } from '@/constants/steppers/hotelSteps';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import { SupportedLocale } from '@/types/supported-locale.type';
import {
  HotelFormDataWithTranslations,
  HotelSchemaWithTranslations,
} from '@/i18n/translation-type-utils';
import { useTranslateFields } from '@/hooks/useTranslate';
import LanguageSwitchWithTranslateBtn from '../translation/LanguageSwitchWithTranslateBtn';
import { MarginRateSchema } from '@/types/common/margin-rate.schema';
import { serializeHotelSupplements } from '@/utils/benefits/hotel/serializeHotelSupplements';

type SubmitFormType = (
  isDraft?: boolean,
) => SubmitHandler<HotelFormDataWithTranslations>;

interface ViewHotelFormProps {
  hotel: HotelResponse;
  suppliers?: {
    id: string;
    name: string;
    currency: string;
  }[];
  language: SupportedLocale;
  setLanguage: React.Dispatch<React.SetStateAction<SupportedLocale>>;
}

const ViewHotelForm = ({
  hotel,
  suppliers,
  language,
  setLanguage,
}: ViewHotelFormProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const [isEditEnabled, setIsEditEnabled] = useState(false);
  const [currStep, setCurrStep] = useState(0);

  const methods = useForm<HotelFormDataWithTranslations>({
    mode: 'onChange',
    resolver: zodResolver(HotelSchemaWithTranslations),
    defaultValues: loadHotelDefaultValues({ hotel, t }),
  });

  const {
    control,
    getValues,
    reset,
    handleSubmit,
    formState: { isValid, errors },
    setValue,
  } = methods;

  const currentName = useWatch({ control, name: 'name' });
  const selectedSupplier = useWatch({ control, name: 'supplier' });
  const selectedCountry = useWatch({ control, name: 'country' });
  const hotelRoomCategories = useWatch({
    control,
    name: 'hotelRoomCategories',
  });

  const { mutateAsync: updateHotel } = useUpdateHotel(language);
  const { mutateAsync: duplicateHotel } = useDuplicateHotel();

  const [isDeletionModalOpen, setIsDeletionModalOpen] = useState(false);
  const [isDuplicationModalOpen, setIsDuplicationModalOpen] = useState(false);

  const hasAtLeastOneCategory = useMemo(
    () =>
      hotelRoomCategories && hotelRoomCategories.length > 0
        ? hotelRoomCategories[0].name && hotelRoomCategories[0].category
        : false,
    [hotelRoomCategories],
  );

  const errorsLength = useMemo(() => Object.keys(errors).length, [errors]);

  const { translateFields, isTranslating } = useTranslateFields({
    targetLanguage: language,
    control,
    setValue,
    fields: [
      'benefit.name',
      'shortDescriptionTitle',
      'shortDescriptionBody',
      'longDescriptionTitle',
      'longDescriptionBody',
    ] as const,
    nestedFields: {
      hotelRoomCategories: ['name', 'shortDescription', 'longDescription'],
    },
  });

  const categoryErrorIndex = useMemo(() => {
    if (errors.hotelRoomCategories) {
      return +Object.keys(errors.hotelRoomCategories)[0];
    }
    return null;
  }, [errors.hotelRoomCategories]);

  const hasNavigatedToErrorStepRef = useRef(false);
  const prevHasErrorsRef = useRef(false);

  useEffect(() => {
    const hasErrors = !!errors.hotelRoomCategories;

    if (
      hasErrors &&
      !prevHasErrorsRef.current &&
      !hasNavigatedToErrorStepRef.current
    ) {
      startTransition(() => {
        setCurrStep(1);
      });
      hasNavigatedToErrorStepRef.current = true;
    }

    // Reset navigation flag when errors clear
    if (!hasErrors && prevHasErrorsRef.current) {
      hasNavigatedToErrorStepRef.current = false;
    }

    prevHasErrorsRef.current = hasErrors;
  }, [errors.hotelRoomCategories]);

  const { toastAsync } = useToast();
  const pastedFieldsState = usePastedFieldsState();

  const submitForm: SubmitFormType =
    (isDraft = true) =>
    async (data) => {
      const formData = new FormData();
      const addIf = (value: string | Blob | undefined, key: string) => {
        if (value !== undefined) formData.append(key, value);
      };
      if (!isDraft) formData.append('status', SupplierStatus.PUBLISHED);
      addIf(data.name, 'name');
      if (data.category)
        formData.append(
          'category',
          getHotelCategoryName(t, data.category.value),
        );
      addIf(data.chain?.value, 'chain');
      addIf(data.supplier?.value, 'supplierId');
      addIf(data.currency?.value, 'currency');
      addIf(data.country?.value, 'country');
      const parsedMarginRate = MarginRateSchema.safeParse(data.marginRate);
      if (parsedMarginRate.success && parsedMarginRate.data !== undefined) {
        formData.append('marginRate', String(parsedMarginRate.data));
      }
      addIf(data.address, 'address');
      addIf(data.postalCode, 'postalCode');
      addIf(data.city?.value, 'city');
      if (data.latitude) formData.append('latitude', String(data.latitude));
      if (data.longitude) formData.append('longitude', String(data.longitude));
      if (data.mainImage && data.mainImage instanceof Blob) {
        formData.append('files', data.mainImage);
        formData.append('hasMainImage', true.toString());
      } else {
        if (data.mainImage)
          formData.append(
            'mainImageUrl',
            getFileKeyFromUrl(data.mainImage as string),
          );
        formData.append('hasMainImage', false.toString());
      }
      if (data.images) {
        data.images
          .filter((image) => image instanceof Blob)
          .forEach((image) => formData.append('files', image));
        formData.append(
          'imagesCount',
          data.images
            .filter((image) => image instanceof Blob)
            .length.toString(),
        );
        data.images
          .filter((image) => !(image instanceof Blob))
          .forEach((image) =>
            formData.append('imagesUrls[]', getFileKeyFromUrl(image as string)),
          );
      }
      addIf(data.shortDescriptionTitle, 'shortDescriptionTitle');
      addIf(data.shortDescriptionBody, 'shortDescriptionBody');
      addIf(data.longDescriptionTitle, 'longDescriptionTitle');
      addIf(data.longDescriptionBody, 'longDescriptionBody');
      if (data.contacts?.coordinates && data.contacts.coordinates.length > 0) {
        formData.append(
          'contactNames',
          JSON.stringify(
            data.contacts.coordinates
              .filter((coordinate) => coordinate.name && coordinate.phoneNumber)
              .map((contact) => contact.name),
          ),
        );
        formData.append(
          'contactPhones',
          JSON.stringify(
            data.contacts.coordinates
              .filter((coordinate) => coordinate.name && coordinate.phoneNumber)
              .map((contact) => contact.phoneNumber),
          ),
        );
      }
      addIf(data.contacts?.website, 'contactWebsite');
      addIf(data.contacts?.mainEmail, 'contactMainEmail');
      addIf(data.contacts?.reservationsEmail, 'contactReservationsEmail');
      addIf(data.contacts?.stockAlertEmail, 'contactStockAlertEmail');
      if (data.contacts?.secondaryEmails)
        formData.append(
          'contactSecondaryEmails',
          JSON.stringify(
            data.contacts.secondaryEmails.map(({ email }) => email),
          ),
        );
      if (data.acceptsChildPricing)
        formData.append(
          'acceptsChildPricing',
          String(data.acceptsChildPricing),
        );
      if (data.childAges && data.childAges.length > 0) {
        formData.append('childAgeFrom', String(data.childAges[0].childAgeFrom));
        formData.append('childAgeTo', String(data.childAges[0].childAgeTo));
        if (data.childAges.length > 1) {
          formData.append(
            'secondChildAgeFrom',
            String(data.childAges[1].childAgeFrom),
          );
          formData.append(
            'secondChildAgeTo',
            String(data.childAges[1].childAgeTo),
          );
        }
      }
      addIf(data.pricingPensionType?.value, 'pricingPensionType');
      addIf(data.shouldFillOtherPensionTypes, 'shouldFillOtherPensionTypes');
      addIf(data.areOtherPensionPricesCommon, 'areOtherPensionPricesCommon');
      if (data.commonPensionPricings) {
        formData.append(
          'commonPensionPricings',
          JSON.stringify(serializePensionPricings(data.commonPensionPricings)),
        );
      }
      addIf(data.pricingType, 'pricingType');
      addIf(data.reducedPriceExists, 'reducedPriceExists');
      if (data.priceReductionType)
        formData.append(
          'isReduction',
          (data.priceReductionType === 'reduction').toString(),
        );
      addIf(data.shouldFillHotelSupplements, 'shouldFillHotelSupplements');
      if (data.hotelSupplements) {
        formData.append(
          'hotelSupplements',
          JSON.stringify(serializeHotelSupplements(data.hotelSupplements)),
        );
      }
      addIf(data.shouldAssociateBenefits, 'shouldAssociateBenefits');
      addIf(data.expertMode, 'expertMode');
      formData.append(
        'associatedBenefits',
        JSON.stringify(
          (data.associatedBenefits ?? []).map(({ benefitId, frequency }) => ({
            benefitId,
            frequency,
          })),
        ),
      );
      if (data.hotelRoomCategories) {
        formData.append(
          'hotelRoomCategories',
          JSON.stringify(
            data.hotelRoomCategories.map((roomCategory) => ({
              ...roomCategory,
              categoryPensionPricings: serializePensionPricings(
                roomCategory.categoryPensionPricings ?? [],
              ),
              pricings: roomCategory.tarifs.map((tarif) => ({
                name: tarif.name,
                defaultPricesExists: tarif.defaultPricesExist,
                defaultAdultPrice: numberIfNotUndefined(
                  tarif.defaultPrices?.price,
                ),
                defaultFirstSpecialPrice: numberIfNotUndefined(
                  tarif.defaultPrices?.firstSpecialPrice,
                ),
                defaultSecondSpecialPrice: numberIfNotUndefined(
                  tarif.defaultPrices?.secondSpecialPrice,
                ),
                periods: tarif.periods.map((period) => ({
                  startDate: period.startDate
                    ? new Date(period.startDate)
                    : undefined,
                  endDate: period.endDate
                    ? new Date(period.endDate)
                    : undefined,
                  applicationDays: period.applicationDays,
                })),
                pricePartitions: getAllPricesPartitions(
                  tarif.pricePartitions,
                  roomCategory.roomTypes,
                ),
              })),
              tarifs: undefined,
              category: roomCategory.category?.value ?? undefined,
              complementaryInformation:
                roomCategory.complementaryInformation?.value ?? undefined,
              roomTypes: Object.keys(roomCategory.roomTypes).filter(
                (key) =>
                  roomCategory.roomTypes[
                    key as keyof typeof roomCategory.roomTypes
                  ],
              ),
              hasMainImage: roomCategory.mainImage instanceof Blob,
              imagesCount: roomCategory.images?.length ?? 0,
              mainImageUrl:
                roomCategory.mainImage instanceof Blob ||
                !roomCategory.mainImage
                  ? undefined
                  : getFileKeyFromUrl(roomCategory.mainImage as string),
              imagesUrls:
                roomCategory.images
                  ?.filter((image) => !(image instanceof Blob))
                  .map((image) => getFileKeyFromUrl(image as string)) ?? [],
              images: undefined,
              mainImage: undefined,
            })),
          ),
        );
        data.hotelRoomCategories.forEach((roomCategory) => {
          if (roomCategory.mainImage instanceof Blob)
            formData.append('files', roomCategory.mainImage);
          roomCategory.images
            .filter((image) => image instanceof Blob)
            .forEach((image) => formData.append('files', image));
        });
      }
      formData.append('translations', JSON.stringify(data.translations));
      await toastAsync(
        updateHotel({ benefitId: hotel.benefitId, data: formData }),
        { success: t('benefits.benefitUpdatedSuccess') },
      );
      pastedFieldsState.clearPasted();
      if (!isDraft)
        navigate(
          PATHS.home.benefits.list +
            getListSearchFromLocation(location, buildBenefitsListSearch),
        );
    };

  return (
    <FormProvider {...methods}>
      <PastedFieldsProvider value={pastedFieldsState}>
        <StyledContainer>
          <StyledFormHeaderWithBreadcrumb
            title={currentName || hotel.benefit.name}
            code={formatBenefitCode(hotel.benefit.code)}
            label={<BenefitTypeCell type={hotel.benefit.type} />}
            breadcrumb={
              <StyledButton
                variant="link"
                title={t('common.backToList')}
                className="text-sm text-[#3D3D3D] pl-0"
                onClick={() =>
                  navigate(
                    PATHS.home.benefits.list +
                      getListSearchFromLocation(
                        location,
                        buildBenefitsListSearch,
                      ),
                  )
                }
                icon={<ArrowLeftIcon />}
              />
            }
          >
            <StyledButton
              title={t('common.delete')}
              variant="outlined"
              className="font-semibold gap-1 px-3 py-2 text-gray-800 border-gray-300"
              onClick={() => setIsDeletionModalOpen(true)}
            />
            <StyledButton
              variant="outlined"
              title={t('common.duplicate')}
              className="font-semibold gap-1 px-3 py-2"
              onClick={() => setIsDuplicationModalOpen(true)}
              icon={<CopyIcon className="w-5 h-5 stroke-brand-600" />}
            />
            {!isEditEnabled ? (
              <StyledButton
                title={t('common.edit')}
                onClick={() => setIsEditEnabled(true)}
                className="font-semibold gap-1 px-3 py-2"
                icon={<EditIcon className="w-5 h-5 stroke-white" />}
              />
            ) : (
              <>
                <StyledButton
                  title={t('common.cancel')}
                  onClick={() => {
                    setIsEditEnabled(false);
                    // Back to the saved hotel, not to the stored defaults:
                    // adding tariffs (button or paste) rewrites those.
                    reset(loadHotelDefaultValues({ hotel, t }));
                    pastedFieldsState.clearPasted();
                  }}
                  className="font-semibold gap-1 px-3 py-2"
                />
                <StyledButtonLoader
                  title={t('common.save')}
                  successTitle={t('common.draftSaved')}
                  onClick={
                    hotel.benefit.status === SupplierStatus.DRAFT
                      ? () => {
                          submitForm(true)(getValues());
                        }
                      : handleSubmit(submitForm(true))
                  }
                  disabled={
                    hotel.benefit.status === SupplierStatus.DRAFT
                      ? currentName == undefined ||
                        selectedSupplier == undefined ||
                        selectedCountry == undefined
                      : !isValid
                  }
                  className="font-semibold gap-1 px-3 py-2"
                  icon={<SaveIcon className="w-5 h-5" />}
                />
              </>
            )}
          </StyledFormHeaderWithBreadcrumb>
          <StyledStepper
            activeStep={currStep}
            steps={getTranslatedConst(HOTEL_STEPS)}
            setStep={(step: number) => setCurrStep(step)}
          />
          <LanguageSwitchWithTranslateBtn
            translateFields={() =>
              translateFields({
                allNestedFields: true,
                translateToAllLocales: true,
              })
            }
            isTranslating={isTranslating}
            language={language}
            setLanguage={setLanguage}
            hideTranslateBtn={!isEditEnabled || currStep > 1}
          />
          {currStep === 0 ? (
            <>
              <HotelConfigurationStep
                control={control}
                suppliers={suppliers}
                language={language}
                errorsLength={errorsLength}
                isViewMode={!isEditEnabled}
                translateFields={(fields) =>
                  translateFields({ fieldsToTranslate: fields })
                }
                isTranslating={isTranslating}
              />
              {isEditEnabled && (
                <div className="flex flex-row-reverse px-6 pb-6 md:w-fit md:sticky md:left-full md:bottom-16">
                  <StyledButtonLoader
                    title={t('common.save')}
                    successTitle={t('common.draftSaved')}
                    onClick={
                      hotel.benefit.status === SupplierStatus.DRAFT
                        ? () => {
                            submitForm(true)(getValues());
                          }
                        : handleSubmit(submitForm(true))
                    }
                    disabled={
                      hotel.benefit.status === SupplierStatus.DRAFT
                        ? currentName == undefined ||
                          selectedSupplier == undefined ||
                          selectedCountry == undefined
                        : !isValid
                    }
                    className="font-semibold gap-1 px-3 py-2"
                    icon={<SaveIcon className="w-5 h-5" />}
                  />
                </div>
              )}
              <div className="flex flex-row-reverse p-6 md:w-fit md:sticky md:left-full md:bottom-0">
                <StyledButton
                  title={
                    hasAtLeastOneCategory
                      ? t('common.seeRoomCategories')
                      : t('common.addRoomCategory')
                  }
                  onClick={() => {
                    setCurrStep(1);
                    window.scrollTo(0, 0);
                  }}
                  disabled={
                    currentName == undefined ||
                    selectedSupplier == undefined ||
                    selectedCountry == undefined
                  }
                  className="font-semibold gap-1 px-3 py-2"
                  icon={<ArrowRightIcon className="w-5 h-5" />}
                  iconPosition="right"
                />
              </div>
            </>
          ) : currStep === 1 ? (
            <>
              <HotelRoomsCategoriesList
                control={control}
                categoryErrorIndex={categoryErrorIndex}
                isViewMode={!isEditEnabled}
                language={language}
              />
              {isEditEnabled && (
                <div className="flex flex-row-reverse px-6 pb-6 md:w-fit md:sticky md:left-full md:bottom-16">
                  <StyledButtonLoader
                    title={t('common.save')}
                    successTitle={t('common.draftSaved')}
                    onClick={
                      hotel.benefit.status === SupplierStatus.DRAFT
                        ? () => {
                            submitForm(true)(getValues());
                          }
                        : handleSubmit(submitForm(true))
                    }
                    disabled={
                      hotel.benefit.status === SupplierStatus.DRAFT
                        ? currentName == undefined ||
                          selectedSupplier == undefined ||
                          selectedCountry == undefined
                        : !isValid
                    }
                    className="font-semibold gap-1 px-3 py-2"
                    icon={<SaveIcon className="w-5 h-5" />}
                  />
                </div>
              )}
              <div className="flex flex-row-reverse px-6 pb-6 md:w-fit md:sticky md:left-full md:bottom-0">
                <StyledButton
                  title={t('common.summary')}
                  icon={<ArrowRightIcon className="w-5 h-5" />}
                  iconPosition="right"
                  className="font-semibold gap-1 px-3 py-2"
                  onClick={() => {
                    setCurrStep(2);
                    window.scrollTo(0, 0);
                  }}
                />
              </div>
            </>
          ) : (
            <>
              <HotelPricingSummary control={control} />
              <div className="flex flex-row-reverse px-6 pb-6">
                <StyledButtonLoader
                  title={t('common.save')}
                  successTitle={t('common.draftSaved')}
                  onClick={
                    hotel.benefit.status === SupplierStatus.DRAFT
                      ? () => {
                          submitForm(true)(getValues());
                        }
                      : handleSubmit(submitForm(true))
                  }
                  disabled={
                    hotel.benefit.status === SupplierStatus.DRAFT
                      ? currentName == undefined ||
                        selectedSupplier == undefined ||
                        selectedCountry == undefined
                      : !isValid
                  }
                  className="font-semibold gap-1 px-3 py-2"
                  icon={<SaveIcon className="w-5 h-5" />}
                />
              </div>
              {hotel.benefit.status === SupplierStatus.DRAFT && (
                <div className="flex flex-row-reverse p-6 md:sticky md:bottom-0 z-10 bg-white">
                  <StyledButtonLoader
                    title={t('hotels.publishHotel')}
                    className="font-semibold gap-1 px-3 py-2"
                    onClick={handleSubmit(submitForm(false))}
                    successTitle={t('common.publishedSuccess')}
                    hasSucceeded={!errorsLength}
                    errorTitle={t('common.checkRequiredFields')}
                    icon={
                      <PlusIcon
                        className={clsx('w-5 h-5', {
                          'fill-white': isValid,
                          'fill-gray-400': !isValid,
                        })}
                      />
                    }
                    iconPosition="right"
                  />
                </div>
              )}
            </>
          )}
          {isDeletionModalOpen && hotel.benefitId && (
            <BenefitDeletionModal
              benefitId={hotel.benefitId}
              closeModal={() => {
                setIsDeletionModalOpen(false);
              }}
              onDeleted={() =>
                navigate(
                  PATHS.home.benefits.list +
                    getListSearchFromLocation(
                      location,
                      buildBenefitsListSearch,
                    ),
                )
              }
            />
          )}
          {isDuplicationModalOpen && (
            <ModalDuplication
              actionOnClick={async (selectedFields) => {
                if (hotel.benefitId) {
                  const duplicatedHotel = await duplicateHotel({
                    benefitId: hotel.benefit.id,
                    fields: selectedFields.flatMap((fields) =>
                      fields.split(';'),
                    ),
                  });
                  navigate(
                    PATHS.home.benefits.hotel.view.replace(
                      ':benefitId',
                      duplicatedHotel.benefitId,
                    ),
                    { state: location.state },
                  );
                }
              }}
              closeModal={() => {
                setIsDuplicationModalOpen(false);
              }}
              fields={getTranslatedConst(HOTEL_DUPLICATION_FIELDS).concat(
                hotel.HotelRoomCategory.map((category, index) => ({
                  label: category.name
                    ? `${category.name}`
                    : `Catégorie ${index + 1}`,
                  value: `hotelRoomCategories;${category.id}`,
                })),
              )}
              type="prestation"
              code={hotel.benefit.code}
            />
          )}
        </StyledContainer>
      </PastedFieldsProvider>
    </FormProvider>
  );
};

export default ViewHotelForm;
